import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";

import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import SEO from "../../layout/SEO";
import ContactInformation from "../../layout/ContactInformation";
import StatusScreen from "../../shared/StatusScreen";
import RichContent from "../../shared/RichContent";
import ProjectCard from "../../shared/ProjectCard";
import publicApi from "../../../utils/publicApi";
import { getProjectLinkType, parseProjectLinks } from "../../../utils/projectLinks";
// Import direct (pas via le barrel du DS) : ces pages publiques ne consomment
// pas le design system, seulement ce helper de couleur qui est neutre.
import { projectPalette } from "../../../design-system/components/ProjectCover/projectPalette";

/**
 * /projets/:slug — la page d'un projet.
 *
 * Style historique du front (cf. docs/STYLE_FRONT_LEGACY.md) : hero façon
 * .banner-area, .section-title pour les titres, .primary-btn pour les liens,
 * grille Bootstrap. Les styles vivent dans styles/_projects.scss.
 */

/** Les quatre groupes de stack, dans l'ordre d'affichage. */
const STACK_GROUPS = [
  { key: "technologies", label: "Langages" },
  { key: "frameworks", label: "Frameworks" },
  { key: "databases", label: "Bases de données" },
  { key: "tools", label: "Librairies & outils" },
];

class ProjectDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      project: null,
      related: [],
      loading: true,
      notFound: false,
      lightboxIndex: null,
    };
  }

  componentDidMount() {
    window.resetScript();
    this.loadProject();
    window.addEventListener("keydown", this.handleKeyDown);
  }

  componentDidUpdate(prevProps) {
    // Navigation d'un projet à l'autre via le bloc « à voir aussi ».
    if (prevProps.match.params.slug !== this.props.match.params.slug) {
      window.scrollTo(0, 0);
      this.loadProject();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  handleKeyDown = (event) => {
    if (event.key === "Escape" && this.state.lightboxIndex !== null) {
      this.setState({ lightboxIndex: null });
    }
  };

  loadProject = async () => {
    const { slug } = this.props.match.params;

    try {
      this.setState({ loading: true, notFound: false });
      const response = await publicApi.get(`/api/projects/${slug}`);

      this.setState({
        project: response.data?.project || null,
        related: response.data?.related || [],
        loading: false,
        notFound: !response.data?.project,
      });
    } catch (error) {
      console.error("Error loading project:", error);
      this.setState({ project: null, related: [], loading: false, notFound: true });
    }
  };

  getGallery = () => {
    const gallery = this.state.project?.gallery;
    return Array.isArray(gallery) ? gallery.filter((item) => item && item.url) : [];
  };

  renderHero = () => {
    const { project } = this.state;
    const palette = projectPalette(project);

    // Image de couverture si elle existe, dégradé du projet sinon.
    const background = project.coverUrl
      ? { backgroundImage: `url("${project.coverUrl.replace(/"/g, '\\"')}")` }
      : { backgroundImage: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)` };

    const links = parseProjectLinks(project.links);

    return (
      <section className="project-detail-hero" style={background}>
        <div className="project-detail-hero-overlay" />

        <div className="container">
          <div className="project-detail-hero-inner">
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <p className="link-nav">
                  <span className="box">
                    <Link to="/">Accueil </Link>
                    <Link to="/projets">Projets </Link>
                  </span>
                </p>

                <h1>{project.title}</h1>

                {project.tagline && (
                  <p className="project-detail-tagline">{project.tagline}</p>
                )}

                {(project.role || project.period || project.clientName) && (
                  <ul className="project-detail-meta">
                    {project.role && (
                      <li>
                        <span className="label">Rôle</span>
                        <span className="value">{project.role}</span>
                      </li>
                    )}
                    {project.period && (
                      <li>
                        <span className="label">Période</span>
                        <span className="value">{project.period}</span>
                      </li>
                    )}
                    {project.clientName && (
                      <li>
                        <span className="label">Client</span>
                        <span className="value">{project.clientName}</span>
                      </li>
                    )}
                  </ul>
                )}

                {links.length > 0 && (
                  <div className="project-detail-links">
                    {links.map((link, index) => {
                      const label = link.label || getProjectLinkType(link.type).label;
                      return (
                        <a
                          key={`${link.url}-${index}`}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="primary-btn"
                        >
                          {label}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <span className="project-detail-accent" style={{ background: palette.accent }} />
      </section>
    );
  };

  renderStack = () => {
    const { project } = this.state;
    const groups = STACK_GROUPS.filter((group) => (project[group.key] || []).length > 0);
    if (groups.length === 0) return null;

    return (
      // `.section-gap` plutôt qu'un fond en dur : c'est cette classe que
      // `.AppDark` sait repeindre en mode sombre.
      <section className="project-stack-area section-gap">
        <div className="container">
          <div className="row">
            {groups.map((group) => (
              <div className="col-lg-3 col-md-6" key={group.key}>
                <div className="project-stack-group">
                  <h3>{group.label}</h3>
                  <ul>
                    {project[group.key].map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  renderStory = () => {
    const { project } = this.state;
    if (!project.excerpt && !project.content) return null;

    return (
      <section className="section-gap">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9">
              {project.excerpt && <p className="lead mb-4">{project.excerpt}</p>}
              {project.content && <RichContent content={project.content} />}
            </div>
          </div>
        </div>
      </section>
    );
  };

  renderGallery = () => {
    const gallery = this.getGallery();
    if (gallery.length === 0) return null;

    return (
      <section className="section-gap pt-0">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <div className="section-title">
                <h2>En Images</h2>
              </div>
            </div>
          </div>

          <div className="row">
            {gallery.map((image, index) => (
              <div
                className={`${gallery.length === 1 ? "col-12" : "col-md-6"} project-gallery-item`}
                key={`${image.url}-${index}`}
              >
                <figure>
                  <button
                    type="button"
                    onClick={() => this.setState({ lightboxIndex: index })}
                    aria-label={`Agrandir : ${image.caption || image.alt || `capture ${index + 1}`}`}
                  >
                    <img src={image.url} alt={image.alt || image.caption || ""} loading="lazy" />
                  </button>
                  {image.caption && <figcaption>{image.caption}</figcaption>}
                </figure>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  renderLightbox = () => {
    const { lightboxIndex } = this.state;
    if (lightboxIndex === null) return null;

    const image = this.getGallery()[lightboxIndex];
    if (!image) return null;

    return (
      <div
        className="project-lightbox"
        role="dialog"
        aria-modal="true"
        aria-label={image.caption || image.alt || "Capture du projet"}
        onClick={() => this.setState({ lightboxIndex: null })}
      >
        <button
          type="button"
          className="project-lightbox-close"
          aria-label="Fermer"
          onClick={() => this.setState({ lightboxIndex: null })}
        >
          <i className="lnr lnr-cross" />
        </button>

        <figure onClick={(e) => e.stopPropagation()}>
          <img src={image.url} alt={image.alt || image.caption || ""} />
          {image.caption && <figcaption>{image.caption}</figcaption>}
        </figure>
      </div>
    );
  };

  renderRelated = () => {
    const { related } = this.state;
    if (related.length === 0) return null;

    return (
      <section className="work-area project-related-area section-gap">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="section-title">
                <h2>À Voir Aussi</h2>
              </div>
            </div>
          </div>

          <div className="row">
            {related.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                className="col-lg-6 col-md-6 col-sm-12"
              />
            ))}
          </div>
        </div>
      </section>
    );
  };

  render() {
    const { project, loading, notFound } = this.state;

    if (loading) {
      return (
        <StatusScreen
          seo={{ title: "Chargement du projet" }}
          kicker="Projet"
          title="Un instant…"
          lead="On récupère la fiche du projet."
          loading
          modeDark={true}
        />
      );
    }

    if (notFound || !project) {
      return (
        <StatusScreen
          seo={{ title: "Projet introuvable" }}
          kicker="Erreur 404"
          title={<>Ce projet <em>n'existe pas</em>.</>}
          lead="Le lien est peut-être périmé, ou le projet n'est pas encore publié."
          primary={{ to: "/projets", label: "Voir tous les projets" }}
          suggestionsTitle="À voir à la place"
          modeDark={true}
        />
      );
    }

    return (
      <>
        <SEO
          title={project.metaTitle || project.title}
          description={project.metaDescription || project.excerpt || project.tagline}
          image={project.coverUrl || project.thumbUrl}
          type="article"
          keywords={[
            project.title,
            ...(project.technologies || []),
            ...(project.frameworks || []),
            "projet",
          ].join(", ")}
        />
        <Header active="Projets" modeDark={true} />

        <div className="project-detail-area">
          {this.renderHero()}
          {this.renderStack()}
          {this.renderStory()}
          {this.renderGallery()}
          {this.renderRelated()}
          {this.renderLightbox()}
        </div>

        <ContactInformation />

        <Footer />
      </>
    );
  }
}

export default withRouter(ProjectDetail);
